import Intercom, { update, onShow, onHide } from '@intercom/messenger-js-sdk';
import React, { useCallback, useContext, useEffect, useLayoutEffect, useState } from 'react';
import { useLocation } from 'react-router';

// Contexts
import MobileContext from 'Shared/contexts/MobileContext';

// Hooks
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useDOMObserver } from 'Shared/utils/hooks/use-dom-observer';
import { useApp } from 'Shared/utils/hooks/use-app';

const INTERCOM_Z_INDEX_DEFAULT = 1040;
const INTERCOM_Z_INDEX_ELEVATED = 1061;

type Props = {
  children: React.ReactNode;
};

const IntercomWrapper = ({ children }: Props) => {
  const { user } = useCurrentUser();
  const location = useLocation();
  const isMobile = useContext(MobileContext);
  const { isZoom, intercomAppId } = useApp();
  const isCommentsPanelOpened = location.pathname.includes('comments');
  const shouldHideIntercom = isMobile || isCommentsPanelOpened || isZoom;

  const getIntercomWrapper = () => document.getElementById('intercom-container');
  const getIntercomStatus = () => Boolean(getIntercomWrapper());
  const [intercomIsActive, setIntercomIsActive] = useState(getIntercomStatus());

  const handleActiveIntercom = useCallback(() => setIntercomIsActive(getIntercomStatus()), [location]);

  const updateIntercomZIndex = (zIndex: string) => {
    const intercomElements = document.querySelectorAll('[class*="intercom-with-namespace"]');

    intercomElements.forEach(element => {
      if (element instanceof HTMLElement) {
        element.style.setProperty('z-index', zIndex, 'important');
      }
    });
  };

  const initilizeIntercom = () => {
    Intercom({
      app_id: intercomAppId,
      alignment: 'right',
      z_index: INTERCOM_Z_INDEX_DEFAULT,
      email: user.email,
      name: user.full_name,
      user_id: user.pi_user_guid,
    });

    onShow(() => {
      // This uses setTimeout to ensure the DOM is ready before updating the z-index
      // This is necessary because Intercom may not be fully rendered when the event is triggered
      // eventually we should remove this setTimeout and use a different trigger source
      setTimeout(() => {
        updateIntercomZIndex(INTERCOM_Z_INDEX_ELEVATED.toString());
      }, 100);
    });

    onHide(() => {
      // This uses setTimeout to ensure the DOM is ready before updating the z-index
      // This is necessary because Intercom may not be fully rendered when the event is triggered
      // eventually we should remove this setTimeout and use a different trigger source
      setTimeout(() => {
        updateIntercomZIndex(INTERCOM_Z_INDEX_DEFAULT.toString());
      }, 100);
    });
  };

  useDOMObserver({ callback: handleActiveIntercom });

  useEffect(() => {
    initilizeIntercom();
  }, []);

  // Updates the location in Intercom when the user navigates since we're in a single page app
  useEffect(() => {
    update({});
  }, [location]);

  useLayoutEffect(() => {
    const intercomWrapper = getIntercomWrapper();
    if (intercomIsActive && shouldHideIntercom) intercomWrapper.style.setProperty('display', 'none', 'important');
    if (intercomIsActive && !shouldHideIntercom) intercomWrapper.style.display = 'block';
  }, [isMobile, shouldHideIntercom, intercomIsActive, location]);

  return <>{children}</>;
};

export default IntercomWrapper;
